import { dayjs } from '@koob/dates';
import useTripStore from '../store/useTripStore';

// Selector hooks over the shared store (US-046: every selector hook lives in
// hooks/, not store/, so components never call `useTripStore(state => …)`
// inline).
export const useExperience = (dayIndex: number) =>
  useTripStore((state) => {
    const day = state.days[dayIndex];
    return day?.experienceId ? state.experiences[day.experienceId] : null;
  });

export const useHotel = (dayIndex: number) =>
  useTripStore((state) => {
    const day = state.days[dayIndex];
    return day?.hotelId ? state.hotels[day.hotelId] : null;
  });

export const useDay = (dayIndex: number) =>
  useTripStore((state) => state.days[dayIndex]);

export const useDays = () => useTripStore((state) => state.days);

export const useSelectedExtras = () =>
  useTripStore((state) =>
    state.days.flatMap((day: any) =>
      Array.isArray(day.selectedExtras) ? day.selectedExtras : [],
    ),
  );

// filtersSlice (US-046) selector hooks — consumed starting US-050/US-052.
export const useTripFilters = () => useTripStore((state) => state.tripFilters);
export const useSetTripFilters = () =>
  useTripStore((state) => state.setTripFilters);
export const useLibraryFilters = () =>
  useTripStore((state) => state.libraryFilters);
export const useSetLibraryFilters = () =>
  useTripStore((state) => state.setLibraryFilters);

// uiSlice (US-046) selector hooks — consumed starting US-050/US-052.
export const useSelectedDay = () => useTripStore((state) => state.selectedDay);
export const useSelectedType = () =>
  useTripStore((state) => state.selectedType);
export const useSelectDay = () => useTripStore((state) => state.selectDay);
export const useResetDaySelection = () =>
  useTripStore((state) => state.resetDaySelection);
export const useFocusDay = () => useTripStore((state) => state.focusDay);
export const useSetFocusDay = () => useTripStore((state) => state.setFocusDay);
export const useListMode = () => useTripStore((state) => state.listMode);
export const useSetListMode = () => useTripStore((state) => state.setListMode);
export const useMapState = () => useTripStore((state) => state.mapState);
export const useSetMapState = () => useTripStore((state) => state.setMapState);
export const useMapZoom = () => useTripStore((state) => state.mapZoom);
export const useSetMapZoom = () => useTripStore((state) => state.setMapZoom);
export const useResetMapView = () =>
  useTripStore((state) => state.resetMapView);
export const useItineraryPinLevel = () =>
  useTripStore((state) => state.itineraryPinLevel);
export const useSetItineraryPinLevel = () =>
  useTripStore((state) => state.setItineraryPinLevel);
export const useSearchHotels = () =>
  useTripStore((state) => state.searchHotels);
export const useSetSearchHotels = () =>
  useTripStore((state) => state.setSearchHotels);
export const useCompareWithVersion = () =>
  useTripStore((state) => state.compareWithVersion);
export const useSetCompareWithVersion = () =>
  useTripStore((state) => state.setCompareWithVersion);
export const useCompareVersionTotalPrice = () =>
  useTripStore((state) => state.compareVersionTotalPrice);
export const useSetCompareVersionTotalPrice = () =>
  useTripStore((state) => state.setCompareVersionTotalPrice);
export const useLastAddedExperienceId = () =>
  useTripStore((state) => state.lastAddedExperienceId);
export const useSetLastAddedExperienceId = () =>
  useTripStore((state) => state.setLastAddedExperienceId);
export const useLoading = () => useTripStore((state) => state.loading);
export const useSetLoading = () => useTripStore((state) => state.setLoading);

export const useHotelGroupForDay = (dayIndex: number, departureDate: any) =>
  useTripStore((state) => {
    const groups: any[] = [];
    let currentGroup: any = null;

    state.days.forEach((day: any, idx: number) => {
      if (day.hotelId && !day.hotelBookedOnMyOwn && !day.hotelIsIncluded) {
        const isSameHotel = currentGroup?.hotelId === day.hotelId;

        if (isSameHotel) {
          currentGroup.totalNights++;
          currentGroup.nights++;
          currentGroup.dayIndices.push(idx);
          currentGroup.groupDayIndices.push(idx);
          currentGroup.endDayIndex = idx;
          currentGroup.endDate = dayjs(departureDate).add(idx + 1, 'days');
        } else {
          if (currentGroup) groups.push(currentGroup);

          currentGroup = {
            hotelId: day.hotelId,
            startDate: dayjs(departureDate).add(idx, 'days'),
            endDate: dayjs(departureDate).add(idx + 1, 'days'),
            totalNights: 1,
            nights: 1,
            dayIndices: [idx],
            groupDayIndices: [idx],
            startDayIndex: idx,
            endDayIndex: idx,
          };
        }
      } else if (currentGroup) {
        groups.push(currentGroup);
        currentGroup = null;
      }
    });

    if (currentGroup) groups.push(currentGroup);

    return groups.find((group) => group.dayIndices.includes(dayIndex));
  });
